import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { bateriaApi, billingApi, linkingApi, shareApi } from "../../api/endpoints.js";
import type { Vinculo } from "../../api/types.js";
import { ScreenHeader, Content } from "../../components/Layout.js";
import { Card, CardLink } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Badge, type BadgeTone } from "../../components/Badge.js";
import { EmptyState } from "../../components/EmptyState.js";
import { InfoBanner, ErrorBanner } from "../../components/Form.js";
import { Loader } from "../../components/Loader.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import { useT, type Translate } from "../../i18n/index.js";
import s from "./TherapistDashboard.module.css";

const CHIP: Record<Vinculo["estado"], { texto: string; tono: BadgeTone }> = {
  pendiente_de_vinculacion: { texto: "Pendiente", tono: "amber" },
  activa: { texto: "Activa", tono: "green" },
  revocada: { texto: "Revocada", tono: "red" },
};

/** Lista embebida de baterías procesadas de un niño, con enlace a su mesa de
 *  revisión. Solo se muestran las que ya tienen pre-informe (procesadas). */
function BateriasExpand({ childId, t }: { childId: string; t: Translate }) {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useQuery(() => bateriaApi.listar(childId), [childId]);
  const revisables = (data ?? []).filter(
    (b) => b.estado === "procesada" || b.estado === "inconcluyente",
  );

  if (loading) return <Loader label={t("common.loading")} />;
  if (error)
    return (
      <>
        <ErrorBanner message={error} />
        <Button variant="ghost" onClick={reload}>
          {t("common.retry")}
        </Button>
      </>
    );
  if (revisables.length === 0)
    return <p className={s.emptyInline}>{t("therapist.baterias.empty")}</p>;

  return (
    <ul className={s.bateriaList}>
      {revisables.map((b) => (
        <li key={b.id} className={s.bateriaRow}>
          <span className={s.bateriaMeta}>
            Banda {b.banda} · {new Date(b.createdAt).toLocaleDateString()}
            {b.modoPrueba ? (
              <Badge tone="red" className={s.inlineBadge}>
                {t("qa.badge")}
              </Badge>
            ) : null}
          </span>
          <Button variant="primary" size="sm" onClick={() => navigate(`/terapeuta/revision/${b.id}`)}>
            {t("therapist.baterias.review")}
          </Button>
        </li>
      ))}
    </ul>
  );
}

export function TherapistDashboard() {
  const t = useT();
  const vinculos = useQuery(() => linkingApi.listar());
  const creditos = useQuery(() => billingApi.misCreditos());
  const pendientes = useQuery(() => shareApi.pendientes());
  const [accionError, setAccionError] = useState<string | null>(null);
  const [ocupadoId, setOcupadoId] = useState<string | null>(null);
  const [expandidoId, setExpandidoId] = useState<string | null>(null);

  async function ejecutar(accion: () => Promise<unknown>, id: string) {
    setAccionError(null);
    setOcupadoId(id);
    try {
      await accion();
      vinculos.reload();
    } catch (e) {
      setAccionError(mensajeDeError(e));
    } finally {
      setOcupadoId(null);
    }
  }

  // Aceptar/rechazar una invitación: al aceptar nace un vínculo, así que
  // recargamos ambas listas.
  async function resolverInvitacion(accion: () => Promise<unknown>, id: string) {
    setAccionError(null);
    setOcupadoId(id);
    try {
      await accion();
      pendientes.reload();
      vinculos.reload();
    } catch (e) {
      setAccionError(mensajeDeError(e));
    } finally {
      setOcupadoId(null);
    }
  }

  const sinCreditos =
    creditos.data && !creditos.data.ilimitado && creditos.data.saldo <= 0;

  return (
    <>
      <ScreenHeader title={t("therapist.dashboard.title")} />
      <Content>
        {sinCreditos ? (
          <InfoBanner tone="warn">
            <div className={s.creditWarn}>
              <span>{t("therapist.dashboard.noCredits")}</span>
              <CardLink to="/terapeuta/creditos" chevron={false} className={s.creditCta}>
                <span className={s.creditCtaText}>{t("therapist.credits.buy")}</span>
              </CardLink>
            </div>
          </InfoBanner>
        ) : null}

        {/* Accesos rápidos que NO son pestañas (las secciones diarias viven en el tab bar). */}
        <div className={s.actions}>
          <CardLink to="/terapeuta/paciente/nuevo" className={s.action}>
            <span className={s.actionIcon} aria-hidden="true">
              ＋
            </span>
            <span className={s.actionLabel}>Nuevo paciente</span>
          </CardLink>
          <CardLink to="/terapeuta/vincular" className={s.action}>
            <span className={s.actionIcon} aria-hidden="true">
              🔗
            </span>
            <span className={s.actionLabel}>{t("therapist.link.title")}</span>
          </CardLink>
          <CardLink to="/terapeuta/pruebas" className={s.action}>
            <span className={s.actionIcon} aria-hidden="true">
              🧩
            </span>
            <span className={s.actionLabel}>{t("therapist.tests.title")}</span>
          </CardLink>
        </div>

        {pendientes.data && pendientes.data.length > 0 ? (
          <Card>
            <h2>{t("therapist.invites.title")}</h2>
            <ul className={s.list}>
              {pendientes.data.map((p) => {
                const ocupado = ocupadoId === p.id;
                return (
                  <li key={p.id} className={s.inviteRow}>
                    <div className={s.inviteMeta}>
                      {t("therapist.invites.item", {
                        bateria: p.bateriaId.slice(0, 8),
                        nino: p.childId.slice(0, 8),
                      })}
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={ocupado}
                      onClick={() => void resolverInvitacion(() => shareApi.aceptar(p.id), p.id)}
                    >
                      {t("therapist.invites.accept")}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={ocupado}
                      onClick={() => void resolverInvitacion(() => shareApi.rechazar(p.id), p.id)}
                    >
                      {t("therapist.invites.reject")}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </Card>
        ) : null}

        <Card>
          <h2>{t("therapist.dashboard.children")}</h2>
          {accionError ? <ErrorBanner message={accionError} /> : null}
          {vinculos.loading ? (
            <Loader label={t("common.loading")} />
          ) : vinculos.error ? (
            <>
              <ErrorBanner message={vinculos.error} />
              <Button variant="ghost" onClick={vinculos.reload}>
                {t("common.retry")}
              </Button>
            </>
          ) : vinculos.data && vinculos.data.length > 0 ? (
            <ul className={s.list}>
              {vinculos.data.map((v) => {
                const chip = CHIP[v.estado];
                const ocupado = ocupadoId === v.id;
                const expandido = expandidoId === v.id;
                return (
                  <li key={v.id} className={s.childCard}>
                    <div className={s.childTop}>
                      <div className={s.childInfo}>
                        <div className={s.childName}>Niño {v.childId.slice(0, 8)}</div>
                        <div className={s.childSub}>Iniciado por {v.iniciadoPor}</div>
                      </div>
                      <Badge tone={chip.tono}>{chip.texto}</Badge>
                    </div>
                    <div className={s.childActions}>
                      {v.estado === "activa" ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setExpandidoId(expandido ? null : v.id)}
                        >
                          {t("therapist.baterias.toggle")}
                        </Button>
                      ) : null}
                      {v.estado === "pendiente_de_vinculacion" ? (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={ocupado}
                          onClick={() => void ejecutar(() => linkingApi.activar(v.id), v.id)}
                        >
                          Activar
                        </Button>
                      ) : null}
                      {v.estado !== "revocada" ? (
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={ocupado}
                          onClick={() => void ejecutar(() => linkingApi.revocar(v.id), v.id)}
                        >
                          {t("common.cancel")}
                        </Button>
                      ) : null}
                    </div>
                    {expandido ? <BateriasExpand childId={v.childId} t={t} /> : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState
              icon="👥"
              title="Aún no tienes niños vinculados"
              description="Vincula un niño o crea un paciente propio para comenzar a evaluar."
              action={
                <CardLink to="/terapeuta/vincular" chevron={false} className={s.emptyCta}>
                  <span className={s.creditCtaText}>{t("therapist.link.title")}</span>
                </CardLink>
              }
            />
          )}
        </Card>
      </Content>
    </>
  );
}
